import React, { useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { LogIn, User, Lock, ArrowRight, AlertCircle } from 'lucide-react';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { AnimatedInput } from '@/components/auth/AnimatedInput';
import { AnimatedButton } from '@/components/auth/AnimatedButton';
import { AuthThemeToggle } from '@/components/auth/ThemeToggle';
import { springConfig, authThemeConfigs } from '@/components/auth/constants';
import { useAuth, getRoleFromUser } from '../contexts/AuthContext';
import { useAuthTheme } from '../contexts/AuthThemeContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { authTheme, setAuthTheme } = useAuthTheme();

  // Get theme configuration
  const themeConfig = authThemeConfigs[authTheme] || authThemeConfigs.invol;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await login(username, password);

      if (result.success) {
        // Check for redirectTo query param (set when user was redirected to login)
        const searchParams = new URLSearchParams(window.location.search);
        const redirectTo = searchParams.get('redirectTo');

        // If valid redirectTo exists, use it; otherwise fall back to role-based redirect
        if (redirectTo && redirectTo.startsWith('/')) {
          navigate({ to: redirectTo });
        } else {
          const role = getRoleFromUser(result.user);

          if (role === 'admin') {
            navigate({ to: '/admin' });
          } else if (role === 'lawyer') {
            navigate({ to: '/lawyer' });
          } else if (role === 'client') {
            navigate({ to: '/client' });
          } else {
            navigate({ to: '/' });
          }
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Theme-aware icon styling
  const iconClasses = themeConfig.iconPrimaryIsGradient
    ? `bg-gradient-to-br ${themeConfig.iconPrimary}`
    : themeConfig.iconPrimary;

  const iconShadow = authTheme === 'drboese'
    ? 'shadow-yellow-500/25'
    : 'shadow-blue-500/25';

  return (
    <AuthLayout variant="login" theme={authTheme}>
      <div className="w-full max-w-md mx-auto">
        {/* Glassmorphism card with gradient border */}
        <div className={`relative p-[1px] rounded-2xl bg-gradient-to-r ${themeConfig.cardBorder}`}>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8">
            {/* Header with icon */}
            <div className="flex flex-col items-center mb-8">
              <motion.div
                className={`w-14 h-14 rounded-2xl ${iconClasses} flex items-center justify-center mb-4 shadow-lg ${iconShadow}`}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={springConfig.bouncy}
              >
                <LogIn className={`h-7 w-7 ${themeConfig.iconColor}`} />
              </motion.div>
              <motion.h2
                className="text-2xl font-bold text-slate-900"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                Welcome back
              </motion.h2>
              <motion.p
                className="text-slate-500 mt-1"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                Sign in to your account
              </motion.p>
            </div>

            {/* Error alert */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  className="mb-6"
                >
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        Sign in failed
                      </p>
                      <p className="text-sm text-red-600 mt-0.5">{error}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <AnimatedInput
                  id="username"
                  label="Username"
                  placeholder="Enter your username"
                  icon={<User className="h-4 w-4" />}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  required
                  autoComplete="username"
                  theme={authTheme}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
              >
                <AnimatedInput
                  id="password"
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  showPasswordToggle
                  icon={<Lock className="h-4 w-4" />}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  autoComplete="current-password"
                  theme={authTheme}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="pt-2"
              >
                <AnimatedButton
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  loading={loading}
                  disabled={!username || !password}
                  icon={<ArrowRight className="h-4 w-4" />}
                  theme={authTheme}
                >
                  Sign In
                </AnimatedButton>
              </motion.div>
            </form>

            {/* Register link */}
            <motion.div
              className="mt-8 pt-6 border-t border-slate-100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
            >
              <p className="text-center text-sm text-slate-500">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className={`font-medium ${themeConfig.link} ${themeConfig.linkHover} relative group inline-flex items-center gap-1`}
                >
                  Create one
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  <span className={`absolute bottom-0 left-0 w-0 h-0.5 ${themeConfig.linkUnderline} group-hover:w-full transition-all duration-300`} />
                </Link>
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Theme toggle */}
      <AuthThemeToggle currentTheme={authTheme} onToggle={setAuthTheme} />
    </AuthLayout>
  );
};

export default LoginPage;
